import React from 'react';
import { chartColors, themeColors } from '../../../constants';
import {
  makeStyles,
  Theme,
  createStyles,
  Typography,
  Button,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { IChartProps, IReduxState } from '../../../types';
import { CardLayout } from '../CardLayouts/';
import { FormatHelper } from '../../../helpers';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    lastUpdate: {
      position: 'absolute',
      left: theme.spacing(1),
      top: 0,
      color: themeColors.white,
    },
    infoCircle: {
      position: 'absolute',
      right: theme.spacing(1),
      top: 0,

      minWidth: 0,
      width: 0,
      height: 0,
      
      color: chartColors.rmYellow,
      border: `1px solid ${chartColors.rmYellow}`,
      borderRadius: '2rem',
      padding: '1rem',

      zIndex: 30,

      display: 'flex',
      alignItems: 'center',
      justifyItems: 'center'
    },
    infoCircleText: {
      textTransform: 'lowercase'
    },
    infoboxContainer: {
      position: 'absolute',
      right: 0,
      overflow: 'visible',
      margin: '10px',
      zIndex: 40,
    },
    infobox: {
      border: '1px solid black',
      borderRadius: '2rem',
      padding: '1rem',
      
      color: themeColors.white,
      background: theme.palette.background.paper
    },
    gaugeWrapper: {
      height: 175,
      width: 200,
    },
    title: {
      marginTop: '1rem',
    },
    titleWrapper: {
      margin: `${theme.spacing(2)}px 0 ${theme.spacing(1)}px 0`,
      [theme.breakpoints.up('md')]: {
        margin: 0,
      },
      [theme.breakpoints.up('lg')]: {
        margin: `${theme.spacing(2)}px 0 ${theme.spacing(1)}px 0`,
      },
    },
    display: {
      margin: theme.spacing(1),
    },
  })
);

export default function ThroughputDisplay(props: IChartProps) {
  const { isPdfReport } = props;
  const classes = useStyles();
  const { t } = useTranslation();

  const formatHelper = new FormatHelper(t);

  const averageThroughputs = useSelector(
    (state: IReduxState) => state.machineDetails.averageThroughputs
  );
  
  const isHybrid = useSelector(
    (state: IReduxState) =>
      // !!state.machineDetails.machine.telemetrySnapshot?.isHybrid
      !!state.machineDetails.machine.isHybrid
  );

  const hasWeightData = useSelector(
    (state: IReduxState) => !!state.machineDetails.machine.hasWeightData
  );

  console.log("hasWeightData: " + hasWeightData);

  // default: 4
  const maxBeltScales = useSelector(
    (state: IReduxState) => state.machineDetails.machine.machineType.maxBeltScales ?? 4
  )

  const {
    weightLoad1,
    weightLoad2,
    weightLoad3,
    weightLoad4,
  } = averageThroughputs;

  const totalWeightLoad1 = useSelector(
    (state: IReduxState) => state.machineDetails.totalWeightLoad1
  );

  const totalWeightLoad2 = useSelector(
    (state: IReduxState) => state.machineDetails.totalWeightLoad2
  );
  const totalWeightLoad3 = useSelector(
    (state: IReduxState) => state.machineDetails.totalWeightLoad3
  );
  const totalWeightLoad4 = useSelector(
    (state: IReduxState) => state.machineDetails.totalWeightLoad4
  );

  console.log(totalWeightLoad1);
  console.log(totalWeightLoad2);

  const weightDataContent : boolean = 
    (!!totalWeightLoad1 && !!totalWeightLoad1.value && totalWeightLoad1.value > 0) 
    || (!!totalWeightLoad2 && !!totalWeightLoad2.value && totalWeightLoad2.value > 0);

  console.log("weightDataContent: " + weightDataContent);

  const [infoboxVisible, setInfoboxVisibility] = React.useState(false);
  const toggleInfobox = () => {
    setInfoboxVisibility(visible => !visible);
  };

  // console.log("infoboxVisible : " + infoboxVisible);

  const infobox = props.isPdfReport
    ? undefined
    : (<React.Fragment>
        <Button className={classes.infoCircle} onClick={toggleInfobox}>
          <Typography className={classes.infoCircleText}>
            i
          </Typography>
        </Button>
        
        {infoboxVisible &&
          <div className={classes.infoboxContainer}>
            <div className={classes.infobox}>
              {/* <p>
                {t('graph.noThroughputData')}
              </p> */}

              <p>
                {t('graph.throughputInfo')}
              </p>

              {/* <ul>
                {Object.keys(performanceIndicatorDefinitions).map((key) => {
                  const level = key as PerformanceIndicatorLevel;
                  const definition = performanceIndicatorDefinitions[level];
                  const color = performanceIndicatorColors[level].label;

                  return (
                    <li key={`graph.performanceIndicatorLevels.${level}`}>
                      <span>
                        {t('graph.performanceIndicatorTooltip.engineLoad')} {definition}
                      </span>
                      &nbsp;=&nbsp;
                      <span style={{ color }}>
                        {t(`graph.performanceIndicatorLevels.${level}`)}
                      </span>
                    </li>
                  );
                })}
              </ul> */}
            </div>
          </div>}
      </React.Fragment>);

  return (
    <CardLayout
      cardStyle={{
        boxShadow:
          isHybrid && !isPdfReport
            ? `0.25rem 0.25rem 0 0 ${chartColors.electricBlue}`
            : 'none',
          position: 'relative', // notwendig für das Overlay
          overflow: 'hidden',
      }}
    >
      {/* Frosted Glass Overlay */}
      {!hasWeightData && !weightDataContent && (
        <>
          {infobox}

          <div
            style={{
              position: 'absolute',
              top: -14,
              left: 0,
              right: 0,
              bottom: -17,
              backdropFilter: 'blur(6px)',
              backgroundColor: 'rgba(255, 255, 255, 0.3)',
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none', // verhindert Klicks
              textAlign: 'center',
            }}
          >
            <Typography variant="h5" style={{ color: '#333' }}>
              {t('graph.throughput')}<br/>
              {t('graph.noThroughputData')}
            </Typography>

          </div>
        </>
    )}

      <Typography className={classes.lastUpdate} variant="caption">
        {`${t('graph.lastReceivedValue')}: ${FormatHelper.formatTimestamp(averageThroughputs.timestamp)}`}
      </Typography>

      <Typography
        className={classes.title}
        align="center"
        variant="h5"
      >
        {t('graph.throughput')}
      </Typography>

      {!!averageThroughputs ? (
        <React.Fragment>
          <div className={classes.titleWrapper}>
            <Typography align="center" variant="body1">
              {t('graph.mainDischargeConveyor')}
            </Typography>

            <Typography align="center" variant="body1">
              {`${t('graph.average')}: `}
              <span style={{ color: chartColors.cyan }}>
                {formatHelper.formatUnitValue(weightLoad1)}
              </span>
            </Typography>

            <Typography align="center" variant="body1">
              {`${t('graph.total')}: `}
              <span style={{ color: chartColors.cyan }}>
                {formatHelper.formatUnitValue(totalWeightLoad1)}
              </span>
            </Typography>
          </div>

          <div className={classes.display}>
            <Typography align="center" variant="body1">
              {t('graph.secondDischargeConveyor')}
            </Typography>

            <Typography align="center" variant="body1">
              {`${t('graph.average')}: `}
              <span style={{ color: chartColors.green }}>
                {formatHelper.formatUnitValue(weightLoad2)}
              </span>
            </Typography>

            <Typography align="center" variant="body1">
              {`${t('graph.total')}: `}
              <span style={{ color: chartColors.green }}>
                {formatHelper.formatUnitValue(totalWeightLoad2)}
              </span>
            </Typography>
          </div>

          {maxBeltScales >= 3 
            ? (
              <div className={classes.display}>
                <Typography align="center" variant="body1">
                  {t('graph.thirdDischargeConveyor')}
                </Typography>

                <Typography align="center" variant="body1">
                  {`${t('graph.average')}: `}
                  <span style={{ color: chartColors.orange }}>
                    {formatHelper.formatUnitValue(weightLoad3)}
                  </span>
                </Typography>

                <Typography align="center" variant="body1">
                  {`${t('graph.total')}: `}
                  <span style={{ color: chartColors.orange }}>
                    {formatHelper.formatUnitValue(totalWeightLoad3)}
                  </span>
                </Typography>
              </div>
            )
            : (<div></div>)
          }

          {maxBeltScales >= 4 
            ? (
              <div className={classes.display}>
                <Typography align="center" variant="body1">
                  {t('graph.fourthDischargeConveyor')}
                </Typography>

                <Typography align="center" variant="body1">
                  {`${t('graph.average')}: `}
                  <span style={{ color: chartColors.lime }}>
                    {formatHelper.formatUnitValue(weightLoad4)}
                  </span>
                </Typography>

                <Typography align="center" variant="body1">
                  {`${t('graph.total')}: `}
                  <span style={{ color: chartColors.lime }}>
                    {formatHelper.formatUnitValue(totalWeightLoad4)}
                  </span>
                </Typography>
              </div>
            )
            : (<div></div>)
          }
        </React.Fragment>
      ) : (
        <Typography
          className={classes.title}
          align="center"
          variant="h4"
        >
          {t('graph.noData')}
        </Typography>
      )}
    </CardLayout>
  );
}
